// filtrosPersistidos.ts — los filtros del admin sobreviven a la recarga.
//
// ── Qué resuelve (round 2026-09-24) ──────────────────────────────────────
// Pedido del dueño: un admin que filtra una tabla, cambia de página y vuelve
// —o recarga, o cierra sesión y entra de nuevo— tenía que volver a poner los
// mismos filtros cada vez. Ahora el último valor de cada filtro queda guardado
// en ESTE navegador, bajo la identidad de ESE usuario, y se restaura solo.
//
// ── Reglas que este módulo hace cumplir ──────────────────────────────────
// 1. SIEMPRE por usuario. La clave es `filtros:v1:<idUsuario>:<pagina.filtro>`.
//    Todas las preferencias que ya existían en el proyecto (cal_panel_filtros,
//    inventario.columnasVisibles, af_arriendos_view_v1…) son claves GLOBALES:
//    en un equipo compartido, el filtro de un admin se lo lleva el siguiente
//    que entre. Acá eso no puede pasar.
// 2. El valor guardado nunca es confiable. Puede venir de una versión anterior
//    de la app, de un id de propiedad que ya se borró o de alguien editando el
//    storage a mano. Toda lectura pasa por `validar`/`deserializar` y, ante la
//    menor duda, se borra la clave y se usa el valor por defecto.
// 3. Los rangos de fecha caducan (CADUCIDAD_FECHAS_DIAS). Casi todos los
//    defaults del sistema son relativos a hoy (el mes actual, hoy-5..hoy+15):
//    restaurar un rango de hace un mes deja la pantalla vacía sin explicación.
// 4. El storage puede fallar (modo privado, cuota llena, storage bloqueado por
//    política). Nada de esto puede romper una página: todo va en try/catch y,
//    si no se puede guardar, la app funciona igual sin recordar nada.
//
// El hook que consume esto es useFiltroPersistido.ts. Ver también
// docs/filtros-persistentes.md.
import dayjs, { Dayjs } from "dayjs";

/** Prefijo de todas las claves. Versionado: si cambia el sobre, sube a v2. */
export const PREFIJO_FILTROS = "filtros:v1:";

/** Días que vive un filtro de fecha guardado antes de volver a su default. */
export const CADUCIDAD_FECHAS_DIAS = 7;

const MS_POR_DIA = 86_400_000;

/** Lo que se escribe en el storage: el valor + cuándo se guardó. */
type Sobre = { at: number; valor: unknown };

/** `filtros:v1:<idUsuario>:<clave>`. La clave es "<pagina>.<filtro>". */
export const claveFiltro = (idUsuario: number | string, clave: string): string =>
  `${PREFIJO_FILTROS}${idUsuario}:${clave}`;

export type OpcionesLectura<T> = {
  /** Si pasaron más días que esto desde el último guardado, se descarta. */
  caducaEnDias?: number;
  /** Forma válida del valor ya deserializado. Si devuelve false, se descarta. */
  validar?: (valor: unknown) => boolean;
  /**
   * Reconstruye el valor desde lo guardado (ej. texto → Dayjs).
   * Devuelve `undefined` si lo guardado no sirve. `null` SÍ es un valor válido
   * (un rango de fechas vacío es una elección del usuario, no un error).
   */
  deserializar?: (crudo: unknown) => T | null | undefined;
};

function borrarClave(claveCompleta: string): void {
  try {
    localStorage.removeItem(claveCompleta);
  } catch {
    // Storage bloqueado: no hay nada que borrar ni forma de saberlo.
  }
}

/**
 * El filtro guardado, o `undefined` si no hay ninguno usable.
 *
 * Cualquier motivo para desconfiar —JSON roto, sobre sin fecha, valor vencido,
 * forma inválida— borra la clave: si no sirve hoy, tampoco va a servir mañana.
 */
export function leerFiltro<T>(
  idUsuario: number | string,
  clave: string,
  opciones: OpcionesLectura<T> = {},
): T | undefined {
  const k = claveFiltro(idUsuario, clave);
  let crudo: string | null = null;
  try {
    crudo = localStorage.getItem(k);
  } catch {
    return undefined;
  }
  if (!crudo) return undefined;
  try {
    const sobre = JSON.parse(crudo) as Sobre | null;
    if (!sobre || typeof sobre.at !== "number") {
      borrarClave(k);
      return undefined;
    }
    if (
      opciones.caducaEnDias != null &&
      Date.now() - sobre.at > opciones.caducaEnDias * MS_POR_DIA
    ) {
      borrarClave(k);
      return undefined;
    }
    const valor = opciones.deserializar
      ? opciones.deserializar(sobre.valor)
      : (sobre.valor as T);
    if (valor === undefined) {
      borrarClave(k);
      return undefined;
    }
    if (opciones.validar && !opciones.validar(valor)) {
      borrarClave(k);
      return undefined;
    }
    return valor as T;
  } catch {
    borrarClave(k);
    return undefined;
  }
}

/**
 * Guarda el filtro. Nunca lanza: si el valor no es serializable o el navegador
 * no deja escribir, la app sigue andando sin recordar ese filtro.
 */
export function guardarFiltro<T>(
  idUsuario: number | string,
  clave: string,
  valor: T,
  serializar?: (valor: T) => unknown,
): void {
  let texto: string | undefined;
  try {
    const sobre: Sobre = {
      at: Date.now(),
      valor: serializar ? serializar(valor) : valor,
    };
    texto = JSON.stringify(sobre);
  } catch {
    // Valor no serializable (Set, Map, referencia circular): no se guarda.
    return;
  }
  if (texto === undefined) return;
  const k = claveFiltro(idUsuario, clave);
  try {
    localStorage.setItem(k, texto);
  } catch {
    // Cuota llena: soltamos los filtros más viejos y reintentamos UNA vez.
    if (podarFiltrosViejos(10) > 0) {
      try {
        localStorage.setItem(k, texto);
      } catch {
        // Sigue sin entrar: se vive sin recordar este filtro.
      }
    }
  }
}

/** Todas las claves de filtros que hay en el storage. */
function clavesDeFiltros(idUsuario?: number | string): string[] {
  const prefijo =
    idUsuario == null ? PREFIJO_FILTROS : `${PREFIJO_FILTROS}${idUsuario}:`;
  const claves: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(prefijo)) claves.push(k);
    }
  } catch {
    return [];
  }
  return claves;
}

/** Borra los `cuantos` filtros más antiguos (de cualquier usuario). */
function podarFiltrosViejos(cuantos: number): number {
  const conFecha = clavesDeFiltros()
    .map((k) => {
      let at = 0;
      try {
        at = (JSON.parse(localStorage.getItem(k) ?? "{}") as Sobre)?.at ?? 0;
      } catch {
        at = 0;
      }
      return { k, at };
    })
    .sort((a, b) => a.at - b.at)
    .slice(0, cuantos);
  conFecha.forEach(({ k }) => borrarClave(k));
  return conFecha.length;
}

/**
 * Borra los filtros guardados de un usuario (o de todos si no se pasa id).
 * Lo usa el menú del header, «Limpiar filtros guardados»: la salida de
 * emergencia cuando un filtro viejo deja una tabla vacía y no se ve por qué.
 */
export function limpiarFiltrosGuardados(idUsuario?: number | string): number {
  const claves = clavesDeFiltros(idUsuario);
  claves.forEach(borrarClave);
  return claves.length;
}

/** Cuántos filtros tiene guardados un usuario. */
export function contarFiltrosGuardados(idUsuario?: number | string): number {
  return clavesDeFiltros(idUsuario).length;
}

// ── Fechas ────────────────────────────────────────────────────────────────
// Dayjs no sobrevive a JSON: JSON.stringify(dayjs()) da un texto y al volver es
// un string, no un Dayjs — la primera llamada a .format() revienta. Por eso los
// filtros de fecha guardan "YYYY-MM-DD" y se reconstruyen al leer.

const ES_FECHA = /^\d{4}-\d{2}-\d{2}$/;

/** Dayjs → "YYYY-MM-DD" (o null si no hay fecha). */
export const serializarDia = (valor: Dayjs | null | undefined): string | null =>
  valor && dayjs.isDayjs(valor) && valor.isValid() ? valor.format("YYYY-MM-DD") : null;

/**
 * "YYYY-MM-DD" → Dayjs. `null` es un valor válido (el usuario borró la fecha);
 * `undefined` significa «lo guardado no sirve».
 */
export const deserializarDia = (crudo: unknown): Dayjs | null | undefined => {
  if (crudo === null) return null;
  if (typeof crudo !== "string" || !ES_FECHA.test(crudo)) return undefined;
  const d = dayjs(crudo);
  // `isValid()` no alcanza: dayjs("2026-13-45") no falla, lo corre a 2027-02-14.
  // Solo aceptamos la fecha si vuelve a escribirse igual que como se guardó.
  return d.isValid() && d.format("YYYY-MM-DD") === crudo ? d : undefined;
};

/** El par de fechas de un RangePicker de AntD. */
export type RangoDayjs = [Dayjs | null, Dayjs | null] | null;

export const serializarRango = (valor: RangoDayjs): unknown =>
  Array.isArray(valor) ? [serializarDia(valor[0]), serializarDia(valor[1])] : null;

export const deserializarRango = (crudo: unknown): RangoDayjs | undefined => {
  if (crudo === null) return null;
  if (!Array.isArray(crudo) || crudo.length !== 2) return undefined;
  const desde = deserializarDia(crudo[0]);
  const hasta = deserializarDia(crudo[1]);
  if (desde === undefined || hasta === undefined) return undefined;
  return [desde, hasta];
};
